import { useState } from 'react';
import { cityLabel, coinCategoryMeta } from '@/pages/le-coin/coinMeta';
import { addCoinReaction, removeCoinReaction, addCoinComment, getCoinComments, reportCoinPost, deleteCoinPostComment } from '@/services/coinService';
import type { ApiCoinComment, CoinPost } from '@/lib/types';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface PostCardProps {
  post: CoinPost;
  index: number;
  onNotify: (msg: string) => void;
}

export default function PostCard({ post, index, onNotify }: PostCardProps) {
  const meta = coinCategoryMeta[post.category];
  const { user } = useAuth();
  const navigate = useNavigate();

  const [liked, setLiked] = useState(post.hasReacted ?? false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [showComments, setShowComments] = useState(false);
  const [apiComments, setApiComments] = useState<ApiCoinComment[]>([]);
  const [localComments, setLocalComments] = useState(post.comments);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const [commentsCount, setCommentsCount] = useState(
    post.commentCount ?? localComments.length
  );

  const totalCommentCount = commentsCount;

  const toggleLike = async () => {
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((c) => (wasLiked ? c - 1 : c + 1));
    try {
      if (wasLiked) {
        await removeCoinReaction(post.id);
      } else {
        await addCoinReaction(post.id);
      }
    } catch {
      // Rollback
      setLiked(wasLiked);
      setLikeCount((c) => (wasLiked ? c + 1 : c - 1));
    }
  };

  const handleToggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoaded) {
      try {
        const fetched = await getCoinComments(post.id);
        setApiComments(fetched);
        setCommentsLoaded(true);
      } catch {
        // ignore, show local comments as fallback
        setCommentsLoaded(true);
      }
    }
  };

  const handleAddComment = async () => {
    const text = newComment.trim();
    if (!text || sendingComment) return;
    setSendingComment(true);
    setNewComment('');
    try {
      const newCmt = await addCoinComment(post.id, text);
      setApiComments((prev) => [...prev, newCmt]);
      setCommentsCount((prev) => prev + 1);
      if (!commentsLoaded) setCommentsLoaded(true);
    } catch {
      // Fallback: add locally
      setLocalComments((prev) => [
        ...prev,
        {
          id: `c-${Date.now()}`,
          authorAlias: user?.name ?? 'Vous',
          content: text,
          time: "À l'instant",
        },
      ]);
    } finally {
      setSendingComment(false);
    }
  };


  // Supprime un commentaire Le Coin appartenant à l'utilisateur connecté.
  // Le backend vérifie également l'autorisation avant d'effectuer la suppression.
  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteCoinPostComment(post.id, commentId);

      setApiComments((prev) =>
        prev.filter((comment) => comment.id !== commentId)
      );

      setCommentsCount((prev) => Math.max(0, prev - 1));

      onNotify('Commentaire supprimé');
    } catch {
      onNotify('Impossible de supprimer le commentaire');
    }
  };

  const handleSignal = async () => {
    try {
      await reportCoinPost(post.id, 'Contenu inapproprié');
    } catch {
      // ignore
    }
    onNotify('Message signalé à la modération');
  };

  const displayedComments = commentsLoaded ? apiComments.map((c) => ({
    id: c.id,
    authorAlias: c.author?.displayName ?? c.author?.name ?? 'Utilisateur',
    content: c.content,
    time: new Date(c.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    isOwner: c.isOwner,
  }))
    : localComments;

  return (
    <article
      className="bg-white border border-background-200 rounded-lg p-4 md:p-5 hover:border-background-300 transition-colors animate-fade-in-up"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Header: avatar + alias + badge */}
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 shrink-0 rounded-full bg-foreground-100 text-foreground-500 flex items-center justify-center">
          <i className="ri-shield-user-line text-lg"></i>
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground-900 truncate">
              {post.authorAlias}
            </span>
            <span className="text-xs text-foreground-400">· {post.time}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${meta.badgeClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`}></span>
              {meta.label}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-map-pin-line text-foreground-400"></i>
              {cityLabel(post.city)}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <p className="mt-3 text-[15px] leading-relaxed text-foreground-800">
        {post.content}
      </p>

      {/* Actions */}
      <div className="mt-4 flex items-center gap-1 border-t border-background-100 pt-3">
        <button
          type="button"
          onClick={toggleLike}
          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${liked
            ? 'text-accent-700 bg-accent-50'
            : 'text-foreground-500 hover:bg-background-100'
            }`}
        >
          <i className={`${liked ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-lg`}></i>
          {likeCount.toLocaleString('fr-FR')}
        </button>

        <button
          type="button"
          onClick={handleToggleComments}
          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-chat-3-line text-lg"></i>
          {totalCommentCount}
        </button>

        <button
          type="button"
          onClick={handleSignal}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
          aria-label="Signaler"
        >
          <i className="ri-flag-line text-base"></i>
          Signaler
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-3 pt-3 border-t border-background-100">
          <div className="flex flex-col gap-3">
            {displayedComments.map((comment) => (
              <div key={comment.id} className="group flex items-start gap-2.5">
                <span className="w-7 h-7 shrink-0 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
                  <i className="ri-shield-user-line text-sm"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-foreground-700">
                      {comment.authorAlias}
                    </span>

                    <span className="text-[11px] text-foreground-400">
                      {comment.time}
                    </span>

                    {comment.isOwner && (
                      <button
                        type="button"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="
                          ml-auto
                          opacity-0
                          group-hover:opacity-100
                          focus:opacity-100
                          text-red-600
                          hover:text-red-700
                          transition-opacity
                          cursor-pointer
                        "
                        aria-label="Supprimer le commentaire"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comment.content}</p>
                </div>
              </div>
            ))}
          </div>

          {/* New comment */}
          <div className="mt-3 flex items-center gap-2">
            <label htmlFor="coin-comment" className="sr-only">
              Commentaire
            </label>

            <input
              id="coin-comment"
              type="text"
              value={newComment}
              readOnly={!user}
              onChange={(e) => {
                if (user) {
                  setNewComment(e.target.value);
                }
              }}
              onClick={() => {
                if (!user) {
                  navigate('/auth');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddComment();
              }}
              placeholder={user ? 'Répondre…' : 'Connecte-toi pour commenter'}
              maxLength={280}
              className={`
              flex-1 min-w-0
              px-4 py-3
              rounded-lg
              border border-background-300
              text-sm text-foreground-900
              placeholder:text-foreground-400
              focus:outline-none
              focus:border-primary-400
              focus:ring-2
              focus:ring-primary-100
              ${!user ? 'bg-background-100 cursor-pointer' : 'bg-background-50'}
            `}
            />

            <button
              type="button"
              onClick={handleAddComment}
              disabled={!newComment.trim() || sendingComment || !user}
              className="px-3 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingComment
                ? <i className="ri-loader-4-line animate-spin"></i>
                : 'Envoyer'
              }
            </button>
          </div>
        </div>
      )}
    </article>
  );
}